<template>
  <hr/>
  <div class="columns is-multiline is-centered" v-if="isLoaded && chartOptions.northOption && chartOptions.southOption">
    <div class="column is-12">
      <h3 class="has-text-centered">{{ $t("profile.ABStatus.impactHF.linearHFChart.sectionHeader") }} </h3>
    </div>
    <div class="column spaced-left is-12-tablet is-6-desktop" v-if="chartOptions.northOption && state?.isForestModel === dataModel.full">
      <h4 class="centered">{{ $t("profile.ABStatus.impactHF.linearHFChart.header", { regionType: "Forested" }) }} </h4>
      <vue-highcharts
        type="chart"
        :options="chartOptions.northOption"
        :redrawOnUpdate="false"
        :oneToOneUpdate="true"
        :animateOnUpdate="true"
        @rendered="onRender"
        @update="onUpdate"
        @destroy="onDestroy"/>
    </div>
    <div class="column spaced-right is-12-tablet is-6-desktop" v-if="chartOptions.southOption && state?.isPrairieModel === dataModel.full">
      <h4 class="centered">{{ $t("profile.ABStatus.impactHF.linearHFChart.header", {regionType: 'Prairie'}) }} </h4>
      <vue-highcharts
        type="chart"
        :options="chartOptions.southOption"
        :redrawOnUpdate="false"
        :oneToOneUpdate="true"
        :animateOnUpdate="true"
        @rendered="onRender"
        @update="onUpdate"
        @destroy="onDestroy"/>
    </div>
    <div class="no-margin-top no-margin-bottom" :class="{'narrow-div': !(state?.isPrairieModel === dataModel.full && state?.isForestModel === dataModel.full)}">
      <figure class="no-margin-bottom no-margin-top">
        <div v-html='$t("profile.ABStatus.impactHF.linearHFChart.caption")'/>
      </figure>
    </div>
    <div class="columns is-multiline is-centered">
      <div class="column spaced-left is-12-tablet is-6-desktop" v-if="chartOptions.northOption && state?.isForestModel === dataModel.full">
        <div class="" v-html="$t('profile.ABStatus.impactHF.linearHFChart.autoFigure.' + valueDiffList.north?.mode,
        { sppName: state?.sppName, regionType: 'forested',
          decreaseListHFType: valueDiffList.north.decreaseStr,
          increaseListHFType: valueDiffList.north.increaseStr,
          leadIncreaseHF: valueDiffList.north.leadIncreaseStr,
          leadDecreaseHF: valueDiffList.north.leadDecreaseStr,
          otherIncreaseHF: valueDiffList.north.otherIncreaseStr,
          otherDecreaseHF: valueDiffList.north.otherDecreaseStr,
        })"></div>
        <div class="interpretion profile richtext profile" v-html="sppContent?.WebFLinearHFGraphText"></div>
      </div>
      <div class="column spaced-left is-12-tablet is-6-desktop"  v-if="chartOptions.southOption && state?.isPrairieModel === dataModel.full">
        <div class="" v-html="$t('profile.ABStatus.impactHF.linearHFChart.autoFigure.' + valueDiffList.south?.mode,
        { sppName: state?.sppName, regionType: 'prairie',
          decreaseListHFType: valueDiffList.south.decreaseStr,
          increaseListHFType: valueDiffList.south.increaseStr,
          leadIncreaseHF: valueDiffList.south.leadIncreaseStr,
          leadDecreaseHF: valueDiffList.south.leadDecreaseStr,
          otherIncreaseHF: valueDiffList.south.otherIncreaseStr,
          otherDecreaseHF: valueDiffList.south.otherDecreaseStr,
        })"></div>
        <!-- display this profile content when it is not mixed type -->
        <div v-if="mode !== 'equal'" class="interpretion profile richtext profile" v-html="sppContent?.WebPLinearHFGraphText"></div>
      </div>
    </div>
  </div>
</template>

<script>
/*
  Linear HF charts show them side by side if there are both north and south charts
  both charts will have same scales.
*/
import { reactive, watch, ref } from 'vue';
import { getLinearData } from '@/services/data/graph-data.js'
import Vue3Highcharts from '@/components/vue3-highcharts/index';
import { useApp } from '@/composables/state/profileState';
import { credits, defaultXAxis, defaultYAxis, yTickLabelFormatter0, DATA_MODEL as dataModel } from '@/config/profile-config';
import { mergeDeep } from '@/lib/common';
import { useI18n } from 'vue-i18n';
export default {
  name: 'habitat-hf-section',
  components: {
    'vue-highcharts': Vue3Highcharts
  },
  props: ['isSpecies'],
  setup (props) {
    const { tm } = useI18n();
    const { state, sppContent } = useApp();
    const isLoaded = ref(0);

    const series = reactive({});

    const yAxisLabel = tm('profile.ABStatus.impactHF.linearHFChart.yAxis');

    const chartOptions = reactive({});
    const chartType = ['north', 'south'];
    const dotSeries = reactive({}); // show a big dot when values beyong [-100, 100]
    const categories = reactive({});
    const colors = reactive({});
    const valueDiffList = reactive({});
    const loadData = async () => {
      if (!state.tsn) {
        return;
      }
      const data = await getLinearData(state.tsn, state.year, 1); // state.year, state.isSpecies); /* state.tsn */
      chartType.forEach(type => {
        series[type] = [];
        dotSeries[type] = [];
        categories[type] = [];
        colors[type] = [];
        valueDiffList[type] = {
          sum: 0,
          list: [],
        };
        data[type] && data[type].forEach(row => {
          const orgY = row.value;
          const scaledY = orgY > 100 ? 100 : (orgY < -100 ? -100 : orgY)
          /* make values within [-100, 100], but keep the origialY for display */

          /*

          for data labels, first make sure the labels are next to the x-axis.
          then move the labels by a fixed offset depends on the y values. e.g.
          if Y > 0, move down, if Y < 0 then move up, so the labels will be at the blank
          chart area.
          These are hardcoded in each data point.

          */
          series[type].push({
            dataLabels: {
              verticalAlign: (orgY > 0 ? 'bottom' : 'top'),
              x: 0,
              y: (orgY > 0 ? 1 : -1) * 25
            },
            orgY: orgY,
            y: scaledY
          });
          dotSeries[type].push({
            orgY,
            y: orgY > 100 ? 105 : (orgY < -100 ? -105 : null)
          });

          categories[type].push(row.label);
          colors[type].push(row.color);
          valueDiffList[type].list.push({ label: row.label, value: orgY });
          valueDiffList[type].sum += orgY;
        });

        valueDiffList[type].list.sort((a, b) => a.value - b.value);
        valueDiffList[type].increase = valueDiffList[type].list.filter(x => x.value >= 0).map(x => x.label);
        valueDiffList[type].decrease = valueDiffList[type].list.filter(x => x.value < 0).map(x => x.label);
        const diff = valueDiffList[type].increase.length - valueDiffList[type].decrease.length;

        let mode = 'default' + diff;
        switch (diff) {
          case 4:
            mode = 'allIncrease';
            break;
          case -4:
            mode = 'allDecrease';
            break;
          case 2:
            mode = 'moreIncrease';
            break;
          case -2:
            mode = 'moreDecrease';
            break;
          case 0:
            mode = 'equal';
            break;
          default:
            break;
        }
        valueDiffList[type].mode = mode;
        valueDiffList[type].leadIncreaseStr = valueDiffList[type].increase[0]?.toLowerCase();
        valueDiffList[type].otherIncreaseStr = valueDiffList[type].increase?.slice(1).join(', ').replace(/, ([^,]*)$/, ' and $1').toLowerCase();
        valueDiffList[type].leadDecreaseStr = valueDiffList[type].decrease[0]?.toLowerCase();
        valueDiffList[type].otherDecreaseStr = valueDiffList[type].decrease?.slice(1).join(', ').replace(/, ([^,]*)$/, ' and $1').toLowerCase();
        valueDiffList[type].increaseStr = valueDiffList[type].increase?.join(', ').replace(/, ([^,]*)$/, ' and $1').toLowerCase();
        valueDiffList[type].decreaseStr = valueDiffList[type].decrease?.join(', ').replace(/, ([^,]*)$/, ' and $1').toLowerCase();
      });
      // console.log({valueDiffList})
      isLoaded.value++;
      chartOptions.southOption = packageChartOptions('south', yAxisLabel);
      chartOptions.northOption = packageChartOptions('north', yAxisLabel);
    };

    const packageChartOptions = (type, label = '') => {
      return {
        credits,
        chart: {
          type: 'column',
        },
        title: {
          text: '' // chart[type].title
        },
        xAxis: mergeDeep(defaultXAxis, {
          categories: categories[type],
          labels: {
            overflow: 'allow',
            // useHTML: true,
            allowOverlap: false,
            formatter: function () {
              const label = this.axis.defaultLabelFormatter.call(this);
              return label.replace(' ', '<br>');
            },
          }
        }),
        yAxis: mergeDeep(defaultYAxis, {
          min: -110,
          max: 110,
          title: {
            text: label
          },

          // gridLineWidth: 1,
          endOnTick: false,
          startOnTick: false,
          labels: { ...yTickLabelFormatter0, style: { 'font-size': '0.7rem' } },
          // offset: 5
        }),
        plotOptions: {
          column: {
            // grouping: true,
            stacking: 'normal', // required this tomake verticalAlign to work. (datalabels)
            dataLabels: {
              // verticalAlign: 'bottom',
              enabled: true,
              style: {
                color: '#333333'
              },
              formatter: function () {
                return (this.point.y > 0 ? '+' : '') + this.point.orgY.toFixed(2) + '%'; // + useHFCustomIcon(extenedHfCategory, 15, this.point.name)
              }
            }
          },
          line: {
            lineWidth: 0,
            dataLabels: {
              enabled: false
            }
          },
          series: {
            colorByPoint: true,
            colors: colors[type],
            groupPadding: 0.2,
          }
        },
        legend: {
          enabled: false,
          useHTML: true,
          reversed: false,
          align: 'center',
          // width: '60%',
          borderWidth: 1
          // labelFormatter: function () { return useHFCustomIcon(categories[type], 15, this.name); }
        },
        tooltip: {
          backgroundColor: '#FCFFC5',
          headerFormat: '',
          pointFormatter: function () {
            return this.category + ': ' + this.options.orgY.toFixed(2) + '%';
          }
          // pointFormat: `${point.name}: {point.y}%`
        },
        series: [{
          data: series[type]
        }, {
          type: 'line',
          data: dotSeries[type]
        }]
      }
    };

    watch(() => state.tsn, async (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {
        loadData();
      }
    });
    return {
      state,
      sppContent,
      loadData,
      chartOptions,
      isLoaded,
      dataModel,
      valueDiffList,
    }
  },
  created () {
    this.loadData();
  }
}
</script>
